// src/pages/league/CreateLeaguePage.tsx - FIXED to use real API
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { InformationCircleIcon, TrophyIcon } from '@heroicons/react/24/outline';
import BackLink from '../../components/common/BackLink';
import { useAuth } from '../../context/AuthContext';
import { LeagueFormData, ScoringSystem, CreateLeagueData } from '../../types';
import { validateLeagueName } from '../../utils/typeUtils';
import VisibilityChoice from '../../components/league/VisibilityChoice';
import apiService from '../../services/api';

export default function CreateLeaguePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [createdLeague, setCreatedLeague] = useState<{ id: number; invite_code: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState<LeagueFormData>({
    name: '',
    league_type: 'season_long',
    max_teams: 10,
    scoring_system_id: 1,
    draft_date: '',
    is_public: false,
    entry_fee: 0,
    
    // Legacy camelCase compatibility
    description: '',
    leagueType: 'season_long',
    maxTeams: 10,
    scoringSystemId: 1,
    draftDate: '',
    isPublic: false,
    entryFee: 0,
  });
  
  const [scoringSystems, setScoringSystems] = useState<ScoringSystem[]>([]);
  const [loadingScoringSystems, setLoadingScoringSystems] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showScoringDetails, setShowScoringDetails] = useState<number | null>(null);

  useEffect(() => {
    apiService.getScoringSystems()
      .then((systems) => {
        setScoringSystems(systems);
        const defaultSystem = systems.find((s) => s.is_default) ?? systems[0];
        if (defaultSystem) {
          setFormData((prev) => ({
            ...prev,
            scoring_system_id: defaultSystem.id,
            scoringSystemId: defaultSystem.id,
          }));
        }
      })
      .catch(() => {
        // non-fatal — form stays at id:1 default
      })
      .finally(() => {
        setLoadingScoringSystems(false);
      });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = value === '' ? 0 : Number(value);
    }
    
    // Update both snake_case and camelCase versions for compatibility
    const updates: any = { [name]: processedValue };
    
    // Add legacy mappings
    if (name === 'league_type' || name === 'leagueType') {
      updates.league_type = processedValue;
      updates.leagueType = processedValue;
    } else if (name === 'max_teams' || name === 'maxTeams') {
      updates.max_teams = processedValue;
      updates.maxTeams = processedValue;
    } else if (name === 'scoring_system_id' || name === 'scoringSystemId') {
      // Radio inputs hand back a string; the id is compared with === against the
      // numeric system.id, so normalize to a number here (mirrors the modal's
      // Number(e.target.value) select handler).
      const numericId = Number(processedValue);
      updates.scoring_system_id = numericId;
      updates.scoringSystemId = numericId;
    } else if (name === 'draft_date' || name === 'draftDate') {
      updates.draft_date = processedValue;
      updates.draftDate = processedValue;
    } else if (name === 'is_public' || name === 'isPublic') {
      updates.is_public = processedValue;
      updates.isPublic = processedValue;
    } else if (name === 'entry_fee' || name === 'entryFee') {
      updates.entry_fee = processedValue;
      updates.entryFee = processedValue;
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // League name validation
    const nameError = validateLeagueName(formData.name);
    if (nameError) {
      newErrors.name = nameError;
    }

    // Max teams validation - use null coalescing to handle both field names
    const maxTeams = formData.maxTeams ?? formData.max_teams ?? 0;
    if (maxTeams < 4) {
      newErrors.maxTeams = 'League must have at least 4 teams';
    } else if (maxTeams > 16) {
      newErrors.maxTeams = 'League cannot have more than 16 teams';
    }

    // Draft date validation (for season-long leagues)
    const leagueType = formData.leagueType || formData.league_type;
    const draftDate = formData.draftDate || formData.draft_date;
    if (leagueType === 'season_long' && draftDate) {
      const draftDateTime = new Date(draftDate);
      const now = new Date();
      if (draftDateTime < now) {
        newErrors.draftDate = 'Draft date must be in the future';
      }
    }

    // Entry fee validation - use null coalescing to handle both field names
    const entryFee = formData.entryFee ?? formData.entry_fee ?? 0;
    if (entryFee < 0) {
      newErrors.entryFee = 'Entry fee cannot be negative';
    } else if (entryFee > 1000) {
      newErrors.entryFee = 'Entry fee cannot exceed $1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!user) {
      setErrors({ submit: 'You must be logged in to create a league' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      console.log('Creating league with real API...');
      
      // Prepare data for real API call (only snake_case fields)
      const createData: CreateLeagueData = {
        name: formData.name,
        description: formData.description || '',
        league_type: formData.league_type || formData.leagueType || 'season_long',
        max_teams: formData.max_teams || formData.maxTeams || 10,
        scoring_system_id: formData.scoring_system_id || formData.scoringSystemId || 1,
        commissioner_id: user.id,
        season_year: new Date().getFullYear(),
        is_public: formData.is_public ?? formData.isPublic ?? false,
        entry_fee: formData.entry_fee ?? formData.entryFee ?? 0,
        draft_date: formData.draft_date || formData.draftDate || undefined,
      };

      console.log('Sending league data to API:', createData);

      // Call real API to create league
      const newLeague = await apiService.createLeague(createData);

      console.log('League created successfully:', newLeague);

      // Show success state with invite code before navigating
      setCreatedLeague({ id: newLeague.id, invite_code: newLeague.invite_code ?? '', name: newLeague.name });
      
    } catch (error: any) {
      console.error('Failed to create league:', error);
      setErrors({ 
        submit: error.message || 'Failed to create league. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!createdLeague) return;
    const joinUrl = `${window.location.origin}/join/${createdLeague.invite_code}`;
    navigator.clipboard.writeText(joinUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // silently ignore — clipboard not available
      });
  };

  if (createdLeague) {
    const joinUrl = `${window.location.origin}/join/${createdLeague.invite_code}`;
    return (
      <div className="space-y-6">
        {/* Header */}
        <BackLink to="/app/leagues">Back to Leagues</BackLink>

        <div className="max-w-lg mx-auto">
          <div className="card">
            <div className="card-body text-center space-y-6 py-10">
              {/* Success icon */}
              <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto">
                <TrophyIcon className="w-8 h-8 text-success-600" />
              </div>

              <div>
                <h2 className="font-display uppercase text-3xl leading-none text-ink-900 mb-2">League Created!</h2>
                <p className="text-secondary-600">
                  <span className="font-semibold">{createdLeague.name}</span> is ready.
                  Share this link to invite friends:
                </p>
              </div>

              {/* Invite code display */}
              <div className="bg-secondary-100 rounded-none p-4">
                <p className="text-xs text-secondary-500 uppercase tracking-wide mb-1">Invite Code</p>
                {createdLeague.invite_code ? (
                  <code className="text-2xl font-bold tracking-widest text-secondary-900">
                    {createdLeague.invite_code}
                  </code>
                ) : (
                  <p className="text-sm text-danger-600">Invite code unavailable — go to league settings.</p>
                )}
              </div>

              {/* Full join URL */}
              <div className="bg-secondary-50 rounded-none p-3">
                <p className="text-xs text-secondary-500 mb-1">Invite Link</p>
                <code className="text-sm break-all text-secondary-700">{joinUrl}</code>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCopy}
                  className="btn-primary flex-1"
                >
                  {copied ? 'Copied!' : 'Copy Invite Link'}
                </button>
                <button
                  onClick={() => navigate(`/app/leagues/${createdLeague.id}`)}
                  className="btn-outline-secondary flex-1"
                >
                  Go to League
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Masthead */}
      <div>
        <BackLink to="/app/leagues" className="mb-4">Back to Leagues</BackLink>
        <div className="border-b-2 border-ink-900 pb-3">
          <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Create League</h1>
          <p className="text-secondary-600 mt-1">Set up your league, then invite friends with a join link.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-display uppercase text-lg tracking-wide text-ink-900">Basic Information</h2>
          </div>
          <div className="card-body space-y-6">
            {errors.submit && (
              <div className="alert-danger">
                {errors.submit}
              </div>
            )}

            <div>
              <label htmlFor="name" className="form-label">
                League Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className={`form-input ${errors.name ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                placeholder="Enter your league name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <p className="form-error">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="form-label">
                Description <span className="text-secondary-400">(optional)</span>
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="form-input"
                placeholder="Describe your league (optional)"
                value={formData.description || ''}
                onChange={handleChange}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="leagueType" className="form-label">
                  League Type *
                </label>
                <select
                  id="leagueType"
                  name="leagueType"
                  required
                  className="form-select"
                  value={formData.leagueType || formData.league_type || 'season_long'}
                  onChange={handleChange}
                >
                  <option value="season_long">Season-Long (Draft & Manage)</option>
                  <option value="weekly_redraft">Weekly Redraft</option>
                </select>
                <p className="text-xs text-secondary-500 mt-1">
                  {(formData.leagueType || formData.league_type) === 'season_long' 
                    ? 'Draft once, manage all season'
                    : 'Fresh picks every week'
                  }
                </p>
              </div>

              <div>
                <label htmlFor="maxTeams" className="form-label">
                  Number of Teams *
                </label>
                <select
                  id="maxTeams"
                  name="maxTeams"
                  required
                  className={`form-select ${errors.maxTeams ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  value={formData.maxTeams || formData.max_teams || 10}
                  onChange={handleChange}
                >
                  <option value={4}>4 Teams</option>
                  <option value={6}>6 Teams</option>
                  <option value={8}>8 Teams</option>
                  <option value={10}>10 Teams</option>
                  <option value={12}>12 Teams</option>
                  <option value={14}>14 Teams</option>
                  <option value={16}>16 Teams</option>
                </select>
                {errors.maxTeams && (
                  <p className="form-error">{errors.maxTeams}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scoring System */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-display uppercase text-lg tracking-wide text-ink-900">Scoring System</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label htmlFor="scoringSystemId" className="form-label">
                Choose Scoring System *
              </label>
              <div className="space-y-3">
                {loadingScoringSystems ? (
                  <p className="text-sm text-secondary-500">Loading scoring systems...</p>
                ) : scoringSystems.length === 0 ? (
                  <p className="text-sm text-danger-600">Could not load scoring systems. Please try again.</p>
                ) : scoringSystems.map((system) => (
                  <div key={system.id} className="relative">
                    <label className="flex items-start space-x-3 p-4 border border-secondary-200 rounded-none cursor-pointer hover:bg-secondary-50">
                      <input
                        type="radio"
                        name="scoringSystemId"
                        value={system.id}
                        checked={(formData.scoringSystemId || formData.scoring_system_id) === system.id}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-secondary-900">{system.name}</p>
                            <p className="text-sm text-secondary-600">{system.description}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowScoringDetails(showScoringDetails === system.id ? null : system.id)}
                            className="text-primary-600 hover:text-primary-500"
                          >
                            <InformationCircleIcon className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {showScoringDetails === system.id && (
                          <div className="mt-3 p-3 bg-secondary-50 border text-xs">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-secondary-800 mb-2">Kicker Scoring</h4>
                                <ul className="space-y-1 text-secondary-600">
                                  <li>FG &lt;40 yards: {system.fg_under_40} pts</li>
                                  <li>FG 40-49 yards: {system.fg_40_49} pts</li>
                                  <li>FG 50+ yards: {system.fg_50_plus} pts</li>
                                  <li>FG 60+ yards: {system.fg_60_plus} pts</li>
                                  <li>Extra Point: {system.extra_point} pts</li>
                                  <li>Missed FG: {system.fg_miss} pts</li>
                                  <li>Game Winner: +{system.game_winner_bonus} pts</li>
                                  <li>Clutch Kick: +{system.clutch_kick_bonus} pts</li>
                                </ul>
                              </div>
                              <div>
                                <h4 className="font-medium text-secondary-800 mb-2">Punter Scoring</h4>
                                <ul className="space-y-1 text-secondary-600">
                                  <li>Inside 20: +{system.punt_inside_20} pts</li>
                                  <li>50+ yard punt: +{system.punt_50_plus} pts</li>
                                  <li>60+ yard punt: +{system.punt_60_plus} pts</li>
                                  <li>Touchback: {system.touchback_penalty} pts</li>
                                  <li>Return TD: {system.punt_return_td_penalty} pts</li>
                                  <li>Fair Catch: +{system.fair_catch_bonus} pts</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Schedule & Settings */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-display uppercase text-lg tracking-wide text-ink-900">Schedule & Settings</h2>
          </div>
          <div className="card-body space-y-6">
            {(formData.leagueType || formData.league_type) === 'season_long' && (
              <div>
                <label htmlFor="draftDate" className="form-label">
                  Draft Date <span className="text-secondary-400">(optional)</span>
                </label>
                <input
                  type="datetime-local"
                  id="draftDate"
                  name="draftDate"
                  className={`form-input ${errors.draftDate ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  value={formData.draftDate || formData.draft_date || ''}
                  onChange={handleChange}
                />
                {errors.draftDate && (
                  <p className="form-error">{errors.draftDate}</p>
                )}
                <p className="text-xs text-secondary-500 mt-1">
                  Leave blank to schedule later
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="entryFee" className="form-label">
                  Entry Fee
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-secondary-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    id="entryFee"
                    name="entryFee"
                    min="0"
                    max="1000"
                    step="1"
                    className={`form-input pl-7 ${errors.entryFee ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                    placeholder="0"
                    value={formData.entryFee ?? formData.entry_fee ?? 0}
                    onChange={handleChange}
                  />
                </div>
                {errors.entryFee && (
                  <p className="form-error">{errors.entryFee}</p>
                )}
              </div>

              <VisibilityChoice
                isPublic={formData.isPublic ?? formData.is_public ?? false}
                onChange={(v) =>
                  setFormData(prev => ({ ...prev, is_public: v, isPublic: v }))
                }
                idPrefix="create-visibility"
              />
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <Link to="/app/leagues" className="btn-outline-secondary">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="loading-spinner w-4 h-4 mr-2"></div>
                Creating League...
              </div>
            ) : (
              'Create League'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}